import type {
  ItsOneViewFinancialItem,
  ItsOneViewResponse,
  ItsOneViewSectionKey,
} from "@/types/its-one-view";
import { SABIL_SECTION_KEYS, SECTION_LABELS, SECTION_ORDER } from "@/utils/its-one-view-sections";

const DEFAULT_FINANCIAL_SECTION_KEYS = SECTION_ORDER.filter((key) => key !== "receipts");

/** Hardcopy raza form: core household heads, plus niyaaz events that still have dues. */
const RAZA_PRINT_SECTION_KEYS: ItsOneViewSectionKey[] = [
  "fmbTakhmeen",
  ...SABIL_SECTION_KEYS,
  "yearlyNiyaaz",
  "events",
];

/** These heads show the project / niyaaz event name, not a rolled-up section label. */
const NAMED_ITEM_SECTION_KEYS: ItsOneViewSectionKey[] = ["projects", "events"];

export type ItsOneViewCommitmentSummary = {
  key: string;
  label: string;
  total: number;
  paid: number;
  balance: number;
};

type CommitmentSummaryOptions = {
  sectionKeys?: readonly ItsOneViewSectionKey[];
  /** Named-item heads (events/projects) only appear when the grouped balance is > 0. */
  namedItemsWithDuesOnly?: readonly ItsOneViewSectionKey[];
};

function sumPending(items: ItsOneViewFinancialItem[]): number {
  return items.reduce((acc, item) => acc + (item.pendingAmount ?? 0), 0);
}

function sumPaid(items: ItsOneViewFinancialItem[]): number {
  return items.reduce((acc, item) => acc + (item.paidAmount ?? 0), 0);
}

function toSummary(
  key: string,
  label: string,
  items: ItsOneViewFinancialItem[]
): ItsOneViewCommitmentSummary {
  const paid = sumPaid(items);
  const balance = sumPending(items);
  return { key, label, total: paid + balance, paid, balance };
}

function namedItemGroupKey(
  sectionKey: ItsOneViewSectionKey,
  item: ItsOneViewFinancialItem
): string {
  const routeId = item.link.routeId?.trim();
  if (routeId) return `${sectionKey}:${routeId}`;
  return `${sectionKey}:${item.label}`;
}

/** One card/row per project or niyaaz event; family pledges for the same id are summed. */
function expandNamedItemSummaries(
  sectionKey: ItsOneViewSectionKey,
  items: ItsOneViewFinancialItem[],
  duesOnly: boolean
): ItsOneViewCommitmentSummary[] {
  const groups = new Map<string, ItsOneViewFinancialItem[]>();
  for (const item of items) {
    const groupKey = namedItemGroupKey(sectionKey, item);
    const group = groups.get(groupKey);
    if (group) group.push(item);
    else groups.set(groupKey, [item]);
  }

  const fallbackLabel = SECTION_LABELS[sectionKey];
  const summaries: ItsOneViewCommitmentSummary[] = [];
  for (const [groupKey, groupItems] of groups) {
    const summary = toSummary(
      groupKey,
      groupItems.find((item) => item.label.trim())?.label.trim() || fallbackLabel,
      groupItems
    );
    if (duesOnly && summary.balance <= 0) continue;
    summaries.push(summary);
  }
  return summaries;
}

/** Summarize ITS One View financial sections that have any items (paid and unpaid). */
export function buildItsOneViewCommitmentSummaries(
  data: ItsOneViewResponse,
  options?: CommitmentSummaryOptions
): ItsOneViewCommitmentSummary[] {
  const allowedKeys = options?.sectionKeys ?? DEFAULT_FINANCIAL_SECTION_KEYS;
  const duesOnlyKeys = options?.namedItemsWithDuesOnly ?? [];
  const modules: ItsOneViewCommitmentSummary[] = [];

  for (const key of SECTION_ORDER) {
    if (!allowedKeys.includes(key)) continue;
    if (data.sectionAccess[key] === "denied") continue;

    const block = data.sections[key];
    const items = block && "items" in block ? (block.items ?? []) : [];
    if (items.length === 0) continue;

    if (NAMED_ITEM_SECTION_KEYS.includes(key)) {
      modules.push(...expandNamedItemSummaries(key, items, duesOnlyKeys.includes(key)));
      continue;
    }

    modules.push(toSummary(key, SECTION_LABELS[key], items));
  }

  return modules;
}

/** Compact heads for the raza print form. */
export function buildRazaPrintCommitmentSummaries(
  data: ItsOneViewResponse
): ItsOneViewCommitmentSummary[] {
  return buildItsOneViewCommitmentSummaries(data, {
    sectionKeys: RAZA_PRINT_SECTION_KEYS,
    namedItemsWithDuesOnly: ["events"],
  });
}
